import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Sparkles, Menu, X, LayoutDashboard } from 'lucide-react'
import { getSession } from '../../utils/auth'

const navLinks = [
    { name: 'Agent', href: '/agent' },
    { name: 'Features', href: '#features' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'FAQ', href: '#faq' },
]

import { isAdminUser } from '../../utils/admin'

export default function Navbar() {
    const [isScrolled, setIsScrolled] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [isAdmin, setIsAdmin] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Check if user is logged in
    useEffect(() => {
        const checkAuth = async () => {
            const session = await getSession()
            setIsLoggedIn(!!session)
            if (session?.user?.email) {
                setIsAdmin(isAdminUser(session.user.email))
            }
        }
        checkAuth()
    }, [])

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
                  ${isScrolled
                    ? 'bg-slate-950/80 backdrop-blur-xl border-b border-white/10'
                    : 'bg-transparent'}`}
        >
            <div className="section-container">
                <div className="flex items-center justify-between h-16 sm:h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-bold text-white">InkSpot</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            link.href.startsWith('/') ? (
                                <Link
                                    key={link.name}
                                    to={link.href}
                                    className="text-white/70 hover:text-white transition-colors text-sm font-medium"
                                >
                                    {link.name}
                                </Link>
                            ) : (
                                <a
                                    key={link.name}
                                    href={link.href}
                                    className="text-white/70 hover:text-white transition-colors text-sm font-medium"
                                >
                                    {link.name}
                                </a>
                            )
                        ))}
                    </div>

                    {/* Desktop CTA - Show different buttons based on auth state */}
                    <div className="hidden md:flex items-center gap-4">
                        {isLoggedIn ? (
                            <>
                                {isAdmin && (
                                    <Link to="/admin" className="btn-ghost flex items-center gap-2 text-red-400 hover:text-red-300">
                                        <LayoutDashboard className="w-4 h-4" />
                                        Admin
                                    </Link>
                                )}
                                <Link to="/app" className="btn-ghost flex items-center gap-2">
                                    <LayoutDashboard className="w-4 h-4" />
                                    Dashboard
                                </Link>
                                <Link to="/app" className="btn-primary text-sm">
                                    Open Editor
                                </Link>
                            </>
                        ) : (
                            <>
                                <Link to="/signin" className="btn-ghost">
                                    Sign In
                                </Link>
                                <Link to="/signup" className="btn-primary text-sm">
                                    Get Started Free
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="md:hidden p-2 text-white/70 hover:text-white"
                        aria-label="Toggle mobile menu"
                    >
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden py-4 border-t border-white/10">
                        <div className="flex flex-col gap-4">
                            {navLinks.map((link) => (
                                link.href.startsWith('/') ? (
                                    <Link
                                        key={link.name}
                                        to={link.href}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="text-white/70 hover:text-white transition-colors text-sm font-medium py-2"
                                    >
                                        {link.name}
                                    </Link>
                                ) : (
                                    <a
                                        key={link.name}
                                        href={link.href}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="text-white/70 hover:text-white transition-colors text-sm font-medium py-2"
                                    >
                                        {link.name}
                                    </a>
                                )
                            ))}
                            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
                                {isLoggedIn ? (
                                    <>
                                        {isAdmin && (
                                            <Link
                                                to="/admin"
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                className="btn-ghost text-center flex items-center justify-center gap-2 text-red-400"
                                            >
                                                <LayoutDashboard className="w-4 h-4" />
                                                Admin Dashboard
                                            </Link>
                                        )}
                                        <Link
                                            to="/app"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="btn-primary text-center flex items-center justify-center gap-2"
                                        >
                                            <LayoutDashboard className="w-4 h-4" />
                                            Dashboard
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/signin"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="btn-secondary text-center"
                                        >
                                            Sign In
                                        </Link>
                                        <Link
                                            to="/signup"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="btn-primary text-center"
                                        >
                                            Get Started Free
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    )
}
